import React from "react";

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Checkbox, Divider } from "antd";
import { t } from "i18next";
import PropTypes from "prop-types";

import {
  predefinedStructure,
  userStaticData,
  operatorStaticData,
} from "./UsersPermissionsDataStructure";

const parentDivClassName = "inline-flex w-full mb-6";
const divClass =
  "border border-solid rounded p-2 mt-2 border-triple-blue flex-grow";
const textDivClass = "font-light text-triple-white uppercase tracking-widest";

function UsersPermissionsList({ formRef, licensesData }) {
  const { LicenseValue } = formRef.getFieldsValue();
  const staticLicensesData = {
    User: [userStaticData],
    Operator: [operatorStaticData],
  };
  const { User, Operator } =
    Object.keys(licensesData).length === 0 ? staticLicensesData : licensesData;

  const lookupMap = {};
  const data = {
    Result: [
      {
        UserLevelName: User?.find((_) => _)?.UserLevelName,
        UserLevelFeatures: User?.find((_) => _)?.UserLevelFeatures,
      },
      {
        UserLevelName: Operator?.find((_) => _)?.UserLevelName,
        UserLevelFeatures: Operator?.find((_) => _)?.UserLevelFeatures,
      },
    ],
  };

  data.Result.forEach((result) => {
    const userLevelFeatures = result.UserLevelFeatures || [];
    const userLevelName = result.UserLevelName;

    userLevelFeatures.forEach((userLevelFeature) => {
      if (!lookupMap[userLevelFeature.FeatureArea]) {
        lookupMap[userLevelFeature.FeatureArea] = {};
      }

      if (
        !lookupMap[userLevelFeature.FeatureArea][userLevelFeature.FeatureName]
      ) {
        lookupMap[userLevelFeature.FeatureArea][userLevelFeature.FeatureName] =
          {};
      }

      lookupMap[userLevelFeature.FeatureArea][userLevelFeature.FeatureName][
        userLevelName
      ] = true;
    });
  });

  const shouldHighlightAreaName = (area, userLevelName) => {
    return predefinedStructure[area].features.some(
      (feature) => lookupMap[area]?.[feature.name]?.[userLevelName],
    );
  };

  const shouldHighlightFeatureName = (area, featureName, userLevelName) => {
    return lookupMap[area]?.[featureName]?.[userLevelName];
  };

  return (
    <div className={parentDivClassName}>
      <div className={divClass}>
        <span className={textDivClass}>{t("users.permissions")}</span>
        <table className="mt-3">
          <tbody>
            {Object.keys(predefinedStructure).map((area) => (
              <React.Fragment key={area}>
                <tr
                  style={{
                    color: shouldHighlightAreaName(area, LicenseValue)
                      ? "#06a5d3"
                      : "#999898",
                  }}
                >
                  <td>
                    <span className="font-normal">
                      {t(predefinedStructure[area].displayName)}
                    </span>
                    <Divider className="m-0" />
                  </td>
                </tr>
                {predefinedStructure[area].features.map((feature) => (
                  <tr
                    key={feature.name}
                    style={{
                      color: shouldHighlightFeatureName(
                        area,
                        feature.name,
                        LicenseValue,
                      )
                        ? "#f6f6f6"
                        : "#999898",
                    }}
                  >
                    <td>
                      {t(feature.displayName)}
                      <Divider className="m-0" />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`${divClass} mx-1 text-center`}>
        <span className={`${textDivClass} mr-2`}>{t("users.operator")}</span>
        <Checkbox checked={LicenseValue === "Operator"} />
        <table className="mt-3 w-full">
          <tbody>
            {Object.keys(predefinedStructure).map((area) => (
              <React.Fragment key={area}>
                <tr>
                  <td>
                    <span className="font-normal text-triple-background">
                      Area
                    </span>
                    <Divider className="m-0" />
                  </td>
                </tr>
                {predefinedStructure[area].features.map((feature) => (
                  <tr key={feature.name}>
                    <td className="text-center align-middle">
                      {lookupMap[area]?.[feature.name]?.Operator ? (
                        <CheckOutlined
                          className={
                            LicenseValue === "Operator"
                              ? "text-triple-blue"
                              : ""
                          }
                        />
                      ) : (
                        <CloseOutlined />
                      )}
                      <Divider className="m-0" />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className={`${divClass} text-center`}>
        <span className={`${textDivClass} mr-2`}>{t("users.user")}</span>
        <Checkbox checked={LicenseValue === "User"} />
        <table className="mt-3 w-full">
          <tbody>
            {Object.keys(predefinedStructure).map((area) => (
              <React.Fragment key={area}>
                <tr>
                  <td>
                    <span className="font-normal text-triple-background">
                      Area
                    </span>
                    <Divider className="m-0" />
                  </td>
                </tr>
                {predefinedStructure[area].features.map((feature) => (
                  <tr key={feature.name}>
                    <td>
                      {lookupMap[area]?.[feature.name]?.User ? (
                        <CheckOutlined
                          className={
                            LicenseValue === "User" ? "text-triple-blue" : ""
                          }
                        />
                      ) : (
                        <CloseOutlined />
                      )}
                      <Divider className="m-0" />
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

UsersPermissionsList.propTypes = {
  formRef: PropTypes.object,
  licensesData: PropTypes.object,
};

UsersPermissionsList.defaultProps = {
  formRef: {},
  licensesData: {},
};

export default UsersPermissionsList;
